import { useMemo } from 'react';
import React from 'react';
import { motion } from 'framer-motion';
import { Virtuoso } from 'react-virtuoso';
import { UserCommentItemSkeleton } from '@components/main-components/user/UserCommentItem';
import type { SongViewFragment } from '@graphql/reactQuery';
import { getFragment, MintComponentFragmentDoc, SortOrder } from '@graphql/reactQuery';
import { usePopState } from '@hooks/usePopState';
import { CommentBar } from './CommentBar';
import { CommentItem } from './CommentItem';
import { CommentsState, useCommentsState } from './store';
import type { Comment } from './store';
import { useReleaseComments } from './useReleaseComments';

export function Comments({
  releaseFrag,
  tabsHeight,
  tabsParentHeight,
}: {
  releaseFrag: SongViewFragment;
  tabsHeight: number | undefined;
  tabsParentHeight: number | undefined;
}) {
  const mintRelease = getFragment(MintComponentFragmentDoc, releaseFrag);
  const releaseId = mintRelease.id;
  const { isPopStateEvent } = usePopState();
  const snapshot = useCommentsState();
  const { data, loadMore, loading, hasNextPage } = useReleaseComments({
    releaseId,
    pageSize: 20,
    sortDirection: SortOrder.Desc,
  });

  const memoizedFooter = useMemo(() => {
    if (!loading && !hasNextPage) return null;
    return (
      <div className="flex flex-col gap-4 pt-[10px]">
        {[...Array(3)].map((_, index) => (
          <UserCommentItemSkeleton key={index} />
        ))}
      </div>
    );
  }, [hasNextPage, loading]);

  const renderCommentItem = (item: Comment) => {
    return <CommentItem item={item} releaseFrag={releaseFrag} />;
  };

  const hasMinimumCommentsToSticky: boolean = data.length > 1;

  return (
    <div className="flex flex-col">
      <CommentBar
        releaseFrag={releaseFrag}
        sticky={{
          isSticky: hasMinimumCommentsToSticky,
          offset: tabsHeight,
        }}
      />
      <motion.div
        className="-mx-1 overflow-hidden"
        initial={{ minHeight: tabsParentHeight }}
        animate={{ minHeight: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Virtuoso
          useWindowScroll
          data={data}
          itemContent={(_, item) => renderCommentItem(item)}
          endReached={loadMore}
          rangeChanged={({ startIndex }) =>
            (CommentsState.commentsScrollIndices[releaseId] = startIndex)
          }
          components={{
            Footer: () => memoizedFooter,
          }}
          overscan={100}
          // Scroll to global state scroll index if popstate event (user used back or forward button in browser)
          // Used to avoid scroll jumping when user switches between tabs
          // Virtuoso breaks if we pass undefined, so we pass an empty object instead
          {...(isPopStateEvent && snapshot.commentsScrollIndices[releaseId]
            ? {
                initialTopMostItemIndex: snapshot.commentsScrollIndices[releaseId] || 0,
              }
            : {})}
        />
      </motion.div>
    </div>
  );
}
